import { useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { adjustInventoryStock, type InventoryItem } from '@/services/inventory';

interface StockAdjustModalProps {
  item: InventoryItem;
  productName: string;
  sizeLabel: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function StockAdjustModal({
  item,
  productName,
  sizeLabel,
  onClose,
  onSaved,
}: StockAdjustModalProps) {
  const [newAvailable, setNewAvailable] = useState(String(item.quantityAvailable));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const delta = (Number(newAvailable) || 0) - item.quantityAvailable;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const parsed = Number(newAvailable);
    if (Number.isNaN(parsed) || parsed < 0) {
      setError('La cantidad disponible no puede ser negativa.');
      return;
    }

    setSaving(true);
    const { error: adjustError } = await adjustInventoryStock(item, parsed, reason);
    setSaving(false);

    if (adjustError) {
      setError(adjustError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title="Ajustar stock"
      subtitle={`${productName} · Talla ${sizeLabel}`}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-background-50 rounded-lg p-3 text-center">
            <p className="text-xs text-foreground-500">Disponible</p>
            <p className="text-lg font-bold text-foreground-950 mt-0.5">{item.quantityAvailable}</p>
          </div>
          <div className="bg-background-50 rounded-lg p-3 text-center">
            <p className="text-xs text-foreground-500">Reservado</p>
            <p className="text-lg font-bold text-foreground-950 mt-0.5">{item.quantityReserved}</p>
          </div>
          <div className="bg-background-50 rounded-lg p-3 text-center">
            <p className="text-xs text-foreground-500">Consignado</p>
            <p className="text-lg font-bold text-foreground-950 mt-0.5">{item.quantityConsigned}</p>
          </div>
        </div>

        <div>
          <label className={labelCls}>Nuevo disponible *</label>
          <input
            type="number"
            min="0"
            className={inputCls}
            value={newAvailable}
            onChange={(e) => setNewAvailable(e.target.value)}
          />
          <div
            className={`mt-1.5 text-xs font-medium ${
              delta === 0
                ? 'text-foreground-500'
                : delta > 0
                  ? 'text-emerald-600'
                  : 'text-red-600'
            }`}
          >
            {delta === 0
              ? 'Sin cambios en el disponible.'
              : delta > 0
                ? `+${delta} pares respecto al actual.`
                : `${delta} pares respecto al actual.`}
          </div>
        </div>

        <div>
          <label className={labelCls}>Motivo del ajuste</label>
          <textarea
            className={`${inputCls} min-h-[80px] resize-none`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej: conteo físico, merma, pérdida, ajuste de entrada..."
            maxLength={500}
          />
        </div>

        <p className="text-xs text-foreground-400 flex items-start gap-1.5">
          <i className="ri-information-line mt-0.5"></i>
          <span>Este ajuste quedará registrado como un movimiento de inventario para mantener la trazabilidad.</span>
        </p>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...
              </>
            ) : (
              <>
                <i className="ri-save-line mr-1.5"></i>Guardar ajuste
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}